import type { AuthPermissionUser } from '../../services/authPermissions';

const CUSTOMER_ORDER_EDIT_ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF', 'TESTER'];
const CUSTOMER_ORDER_OPERATION_ROLES = ['ADMIN', 'OPERATIONS'];
// Mirrors backend: ORDER_STAFF (Handlowiec) edits lines incl. sale prices and
// the commercial header of HIS OWN orders (ownership is backend-enforced).
const CUSTOMER_ORDER_PRODUCT_EDIT_ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF'];
const CUSTOMER_ORDER_COMMERCIAL_EDIT_ROLES = ['ADMIN', 'OPERATIONS', 'ORDER_STAFF'];

export function isCustomerOrderTesterMode(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'TESTER';
}

export function canCreateCustomerOrders(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_EDIT_ROLES.includes(user?.role || '');
}

export function canEditCustomerOrderDetails(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_EDIT_ROLES.includes(user?.role || '');
}

export function canChangeCustomerOrderStatus(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_EDIT_ROLES.includes(user?.role || '');
}

export function canManageCustomerOrderOperations(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canDeleteCustomerOrders(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canEditCustomerOrderDocumentFields(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canEditCustomerOrderProducts(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_PRODUCT_EDIT_ROLES.includes(user?.role || '');
}

export function canEditCustomerOrderCommercialFields(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_COMMERCIAL_EDIT_ROLES.includes(user?.role || '');
}

// Handlowiec (ORDER_STAFF) edits the rest of the commercial header on his own
// orders, but not the account manager (opiekun) — the owner decision of
// 2026-09-23 keeps order ownership reassignment out of his reach.
export function canEditCustomerOrderAccountManager(user: AuthPermissionUser | null | undefined): boolean {
  return canEditCustomerOrderCommercialFields(user) && user?.role !== 'ORDER_STAFF';
}

// WP13: ADMIN and Handlowiec (own orders) pick the forecast month; OPERATIONS
// and TESTER only read it. Mirrors backend utils/orderPermissions.js.
export function canEditCustomerOrderForecastMonth(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN' || user?.role === 'ORDER_STAFF';
}

export function canManageCustomerOrderSupplierLinks(user: AuthPermissionUser | null | undefined): boolean {
  return CUSTOMER_ORDER_OPERATION_ROLES.includes(user?.role || '');
}

export function canManageOrderNotificationAssignments(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}

export function canDeleteCustomerOrderTransportLink(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}

export function canCreatePartialSettlement(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN' || user?.role === 'OPERATIONS';
}

export function canEnterSettlementFinancials(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}

export function canUseOperationalSettlement(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'OPERATIONS';
}

export function canSettleCustomerOrder(user: AuthPermissionUser | null | undefined): boolean {
  return canCreatePartialSettlement(user);
}

export function canCloseCustomerOrderSettlement(user: AuthPermissionUser | null | undefined): boolean {
  return user?.role === 'ADMIN';
}
